/**
 * CSV to JointJS Renderer
 * Converts grouped CSV layers to JointJS elements
 */

import { dia, shapes } from '@joint/core'
import type { GroupedLayer } from '../../../map-editor-features/csv'
import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import { getAssetPath, getIconSize, getLayerFillColor, getLayerStrokeColor, isPointLayer, isLineLayer } from '../../../map-editor-shared/lib/assetMapper'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'
import { DEFAULT_PREFIXES } from '../../../map-editor-shared/constants/idPrefixes'
import { parkingTypeFromLayer, routeLayerToCategory } from '../../../map-editor-shared/lib/layerUtils'
import type { LayerTypeMapping } from '../../../map-editor-shared/store/objectTypeStore/types'
import { minAreaRect } from './minAreaRect'

interface RenderResult {
  elements: dia.Element[]
  objectsByLayer: Map<string, dia.Element[]>
}

interface TypeMapping {
  entityHandle: string
  type: ObjectType
}

/**
 * LOD (Level of Detail) system
 * Determines rendering complexity based on zoom level
 */
enum LODLevel {
  SIMPLIFIED = 0,  // Zoom out (scale < 0.5): Simple rectangles only
  MEDIUM = 1,      // Medium zoom (0.5 ≤ scale < 1.5): Simplified paths
  DETAILED = 2     // Zoom in (scale ≥ 1.5): Full detail
}

/**
 * Get LOD level based on current zoom scale
 */
function getLODLevel(scale: number): LODLevel {
  if (scale < 0.5) return LODLevel.SIMPLIFIED
  if (scale < 1.5) return LODLevel.MEDIUM
  return LODLevel.DETAILED
}

/** Normalize legacy/variant entity type strings from multiple parser paths. */
export function normalizeEntityType(entityType?: string): 'line' | 'polygon' | 'point' | 'unknown' {
  const t = (entityType || '').toLowerCase()
  if (t === 'line' || t === 'polyline' || t === 'lwpolyline') return 'line'
  if (t === 'polygon') return 'polygon'
  if (t === 'point' || t === 'insert' || t === 'text') return 'point'
  return 'unknown'
}

/**
 * Create elements from grouped layers with type mappings
 * OPTIMIZED: Pre-calculate values, reduce redundant operations
 * LOD: Supports level-of-detail rendering based on zoom scale
 */
export function createElementsFromCSV(
  groupedLayers: GroupedLayer[],
  bounds: { minX: number; maxX: number; minY: number; maxY: number },
  typeMappings?: TypeMapping[],
  renderScale?: number,
  currentZoomScale?: number,
  layerTypeMappings?: LayerTypeMapping[]
): RenderResult {
  const objectsByLayer = new Map<string, dia.Element[]>()

  // Create mapping lookup map
  const mappingMap = new Map<string, ObjectType>()
  if (typeMappings) {
    typeMappings.forEach(mapping => {
      mappingMap.set(mapping.entityHandle, mapping.type)
    })
  }

  // Build typeId → convention layer name lookup for naming
  const typeIdToConventionLayer = new Map<string, string>()
  if (layerTypeMappings) {
    layerTypeMappings.forEach(ltm => {
      typeIdToConventionLayer.set(ltm.typeId, ltm.layerName)
    })
  }

  // OPTIMIZATION: Pre-calculate all transformation constants
  const dataWidth = bounds.maxX - bounds.minX
  const dataHeight = bounds.maxY - bounds.minY
  const TARGET_WIDTH = renderScale ?? 5000
  const scale = dataWidth > 0 ? TARGET_WIDTH / dataWidth : 1

  // DO NOT manually center elements - let paper.scaleContentToFit() handle positioning
  // Position elements starting from (0, 0) and scaleContentToFit will center them properly
  const offsetX = 0
  const offsetY = 0

  // OPTIMIZATION: Pre-calculate canvas height for Y-flip
  const scaledWidth = dataWidth * scale
  const scaledHeight = dataHeight * scale
  const canvasHeight = scaledHeight

  // OPTIMIZATION: Pre-extract bounds values (avoid repeated property access)
  const boundsMinX = bounds.minX
  const boundsMinY = bounds.minY

  // Determine LOD level based on current zoom scale
  const lodLevel = currentZoomScale !== undefined ? getLODLevel(currentZoomScale) : LODLevel.DETAILED

  // Log LOD level being used
  const lodLevelName = lodLevel === LODLevel.SIMPLIFIED ? 'SIMPLIFIED' :
                       lodLevel === LODLevel.MEDIUM ? 'MEDIUM' : 'DETAILED'

  // OPTIMIZATION: Create transform context object (pass all values at once)
  const transformContext = {
    boundsMinX,
    boundsMinY,
    scale,
    canvasHeight,
    offsetX,
    offsetY,
    TARGET_WIDTH,
    lodLevel
  }


  // Collect all elements with their priorities first
  const elementsWithPriority: Array<{ element: dia.Element; priority: number; layer: string }> = []

  log.perf.start('Element creation')

  // Track LOD statistics
  let simplifiedCount = 0
  let mediumCount = 0
  let detailedCount = 0

  // Per-prefix counters for type-based naming (e.g., parkingLocations-1, cctvs-1)
  const prefixCounters = new Map<string, number>()
  let fallbackCounter = 0

  groupedLayers.forEach(layer => {
    const layerElements: dia.Element[] = []

    layer.entities.forEach(entity => {
      const mappedType = mappingMap.get(entity.entityHandle)

      // Skip line entities (2 points) in parking layers — these are internal diagonal lines from CAD
      if (entity.points && entity.points.length <= 2) {
        const typeName = mappedType?.name?.toLowerCase() || ''
        const layerName = layer.layer?.toLowerCase() || ''
        if (typeName.includes('주차') || typeName.includes('parking') || layerName.includes('park')) {
          return
        }
      }

      const element = createElementFromEntity(entity, transformContext, layer.layer, mappedType)

      if (element) {
        // Assign type-based name: {prefix}-{number}
        const data = element.get('data')
        if (data?.properties) {
          let prefix: string | null = null

          // 1. Try convention layer from type mapping
          if (mappedType) {
            const conventionLayer = typeIdToConventionLayer.get(mappedType.id) || ''
            if (conventionLayer && DEFAULT_PREFIXES[conventionLayer]) {
              prefix = DEFAULT_PREFIXES[conventionLayer]
            }
          }

          // 2. Fallback: infer prefix from mapped type name or CSV layer name
          if (!prefix) {
            const lowerLayer = layer.layer.toLowerCase()
            const lowerType = mappedType?.name?.toLowerCase() || ''
            const combined = `${lowerLayer} ${lowerType}`
            if (combined.includes('park') || combined.includes('주차')) prefix = 'parkingLocations'
            else if (combined.includes('cctv')) prefix = 'cctvs'
            else if (combined.includes('비상벨') || combined.includes('emergency')) prefix = 'emergencyBells'
            else if (combined.includes('기둥') || combined.includes('pillar') || combined.includes('column')) prefix = 'columns'
            else if (combined.includes('조명') || combined.includes('light')) prefix = 'lightLines'
          }

          if (prefix) {
            const count = (prefixCounters.get(prefix) ?? 0) + 1
            prefixCounters.set(prefix, count)
            data.properties.name = `${prefix}-${count}`
          } else {
            fallbackCounter++
            data.properties.name = String(fallbackCounter)
          }
          element.set('data', { ...data })
        }
        // Track LOD statistics
        const elementLOD = element.get('data')?.lodLevel
        if (elementLOD === LODLevel.SIMPLIFIED) simplifiedCount++
        else if (elementLOD === LODLevel.MEDIUM) mediumCount++
        else detailedCount++

        // Force z-index 1 for "배경" type, otherwise use mapped priority (default 5 if not set)
        const isBackground = mappedType?.name === '배경' || mappedType?.name === 'background'
        const priority = isBackground ? 1 : (mappedType?.priority ?? 5)
        elementsWithPriority.push({ element, priority, layer: layer.layer })
        layerElements.push(element)
      }
    })

    if (layerElements.length > 0) {
      objectsByLayer.set(layer.layer, layerElements)
    }
  })

  log.perf.end('Element creation')


  // Sort by priority (lower priority first, so they render first/bottom)
  // Elements added later get higher z-index in JointJS
  // So we add low priority (background) first, then high priority (foreground) last
  elementsWithPriority.sort((a, b) => a.priority - b.priority)

  // Extract sorted elements
  const sortedElements = elementsWithPriority.map(item => item.element)

  return { elements: sortedElements, objectsByLayer }
}

/**
 * Simplify path points for medium LOD level
 * Reduces point count by ~50% using Douglas-Peucker-like algorithm
 */
function simplifyPoints(points: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
  if (points.length <= 4) return points // Keep small paths unchanged

  // Simple point reduction: keep every other point plus first and last
  const simplified: Array<{ x: number; y: number }> = [points[0]]
  for (let i = 2; i < points.length - 1; i += 2) {
    simplified.push(points[i])
  }
  simplified.push(points[points.length - 1])

  return simplified
}

/**
 * Create a single JointJS element from entity
 * OPTIMIZED: Use transform context, reduce allocations
 * LOD: Renders different detail levels based on zoom
 */
function createElementFromEntity(
  entity: any,
  transformContext: {
    boundsMinX: number
    boundsMinY: number
    scale: number
    canvasHeight: number
    offsetX: number
    offsetY: number
    TARGET_WIDTH: number
    lodLevel: LODLevel
  },
  layer: string,
  mappedType: ObjectType | undefined
): dia.Element | null {
  const { points, entityHandle } = entity

  if (!points || points.length === 0) {
    return null
  }

  // OPTIMIZATION: Destructure transform context once
  const { boundsMinX, boundsMinY, scale, canvasHeight, offsetX, offsetY, TARGET_WIDTH, lodLevel } = transformContext

  const normalizedEntityType = normalizeEntityType(entity.entityType)
  const isPointEntity = Boolean(entity.isPoint) || normalizedEntityType === 'point' || points.length === 1
  // Layer-based line detection: s-outline / e-outline / l-lightingline / e-innerline 등
  // CSV에 entityType 이 안 실려와도 layer 이름으로 line 판정.
  const isLineEntity = normalizedEntityType === 'line' || (!isPointEntity && isLineLayer(layer))
  const preserveCadGeometry =
    layer.startsWith('p-parking') || layer.startsWith('l-') || layer === 'c-light' || layer === 's-pillar'

  // LOD Level 0 (SIMPLIFIED): Use bounding box only - skip detailed geometry
  // Line entities always use Path (Rectangle is unsuitable for lines)
  if (lodLevel === LODLevel.SIMPLIFIED && !isLineEntity && !isPointEntity && !preserveCadGeometry) {
    // Calculate bbox directly without transforming all points
    let dataMinX = Infinity
    let dataMinY = Infinity
    let dataMaxX = -Infinity
    let dataMaxY = -Infinity

    points.forEach((p: { x: number; y: number }) => {
      if (p.x < dataMinX) dataMinX = p.x
      if (p.x > dataMaxX) dataMaxX = p.x
      if (p.y < dataMinY) dataMinY = p.y
      if (p.y > dataMaxY) dataMaxY = p.y
    })

    // Transform bbox corners
    const minX = (dataMinX - boundsMinX) * scale + offsetX
    const maxX = (dataMaxX - boundsMinX) * scale + offsetX
    const minY = canvasHeight - (dataMaxY - boundsMinY) * scale + offsetY // Y-flip
    const maxY = canvasHeight - (dataMinY - boundsMinY) * scale + offsetY // Y-flip

    const width = maxX - minX || 1
    const height = maxY - minY || 1

    // Transform vertices to same coordinate space as element.position (transformed coords)
    // Without this, vertices stay in raw CAD space while position is in transformed space
    // → export coordinates mismatch with point objects (CCTV, bell, etc.) that always use transformed.
    const transformedVerticesLOD0 = points.map(
      (p: { x: number; y: number }) =>
        [(p.x - boundsMinX) * scale + offsetX, canvasHeight - (p.y - boundsMinY) * scale + offsetY] as [number, number]
    )

    // Determine colors - use new fields with backward compatibility
    let fillColor: string
    let strokeColor: string
    let opacity = mappedType?.opacity ?? 0.8
    let sw = mappedType?.strokeWidth ?? 2
    if (mappedType?.strokeColor || mappedType?.fillColor) {
      strokeColor = mappedType.strokeColor || mappedType.color || '#333'
      fillColor = mappedType.fillColor === 'none' ? 'none' : mappedType.fillColor || mappedType.color || '#4CAF50'
    } else if (mappedType?.color) {
      const colorMode = mappedType.colorMode || 'fill'
      fillColor = colorMode === 'stroke' ? 'none' : mappedType.color
      strokeColor = mappedType.color
    } else {
      fillColor = getLayerFillColor(layer)
      strokeColor = fillColor
    }

    // Force stroke-only for lighting layers in simplified LOD
    if (layer.startsWith('l-') && !isPointEntity) {
      fillColor = 'none'
    }

    const isBackground = mappedType?.name === '배경' || mappedType?.name === 'background'
    const zIndex = isBackground ? 1 : (mappedType?.priority ?? 5)

    // Create simple rectangle for LOD 0
    const rectangle = new shapes.standard.Rectangle({
      id: `${layer}_${entityHandle}`,
      position: { x: minX, y: minY },
      size: { width, height },
      z: zIndex,
      attrs: {
        body: {
          fill: fillColor,
          stroke: strokeColor,
          strokeWidth: sw,
          opacity: opacity,
        },
        label: {
          text: '',
        },
      },
      data: {
        layer,
        entityHandle,
        type: isPointEntity ? 'point' : isLineEntity ? 'line' : 'polygon',
        typeId: mappedType?.id,
        lodLevel: LODLevel.SIMPLIFIED,
        vertices: transformedVerticesLOD0,
        // Snapshot for drag-delta detection in elementConverter (export path)
        originalPosition: { x: minX, y: minY },
        properties: {
          name: '',
          // 주차면 카테고리로 라우팅되는 레이어는 매핑 시 선택한 종류
          // (p-parking-disable, p-parking-electric 등) 를 기반으로
          // router/DB type 컬럼 (REGULAR/COMPACT/HANDICAPPED/ELECTRIC/FAMILY)
          // 을 자동 채움. PropertyEditor 에서 사용자가 바꾸면 그 값이 우선.
          ...(routeLayerToCategory(layer) === 'parkingLocation' && {
            type: parkingTypeFromLayer(layer),
          }),
          ...(entity.parkingType && { spotType: entity.parkingType }),
        },
      },
    })

    if (mappedType) {
      rectangle.prop('objectTypeId', mappedType.id)
    }

    return rectangle
  }

  // LOD Level 1 (MEDIUM) and 2 (DETAILED): Use path geometry
  // Transform coordinates WITH Y-AXIS FLIP to match preview
  // Optimize: single pass for transform + bbox calculation
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  // Apply point simplification for MEDIUM LOD
  const pointsToRender = lodLevel === LODLevel.MEDIUM && !preserveCadGeometry ? simplifyPoints(points) : points

  const transformedPoints = pointsToRender.map((p: { x: number; y: number }) => {
    // OPTIMIZATION: Use destructured values directly (no property access)
    const x = (p.x - boundsMinX) * scale + offsetX
    const y = canvasHeight - (p.y - boundsMinY) * scale + offsetY  // FLIP Y-AXIS + offset

    // Update bbox in same loop (avoid extra iterations)
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y

    return { x, y }
  })

  const width = maxX - minX || 1
  const height = maxY - minY || 1

  // Determine colors based on mapped type - use new fields with backward compatibility
  let fillColor: string
  let strokeColor: string
  const typeOpacity = mappedType?.opacity ?? 0.8
  const typeStrokeWidth = mappedType?.strokeWidth ?? 2

  if (mappedType) {
    if (mappedType.strokeColor || mappedType.fillColor) {
      strokeColor = mappedType.strokeColor || mappedType.color || '#333'
      fillColor = mappedType.fillColor === 'none' ? 'none' : (mappedType.fillColor || mappedType.color || '#4CAF50')
    } else if (mappedType.color) {
      const colorMode = mappedType.colorMode || 'fill'
      fillColor = colorMode === 'stroke' ? 'none' : mappedType.color
      strokeColor = mappedType.color
    } else {
      fillColor = getLayerFillColor(layer)
      strokeColor = getLayerStrokeColor(layer)
    }

    // Force stroke-only for lighting and line entities (no fill)
    if (
      !isPointEntity &&
      (mappedType.name === '조명 프레임' || layer.toLowerCase().includes('lightinglineframe') || layer.startsWith('l-') || isLineEntity)
    ) {
      fillColor = 'none'
    }

    // CRITICAL DEBUG: Log color application for mapped entities
    if (fillColor === '#4CAF50') {
    }
  } else {
    fillColor = getLayerFillColor(layer)
    strokeColor = getLayerStrokeColor(layer)

    // CRITICAL DEBUG: Log when green color is applied without mapping
    if (fillColor === '#4CAF50') {
    }

    // Force stroke-only for lighting and line entities without mapped type
    if (!isPointEntity && (isLineEntity || layer.startsWith('l-'))) {
      fillColor = 'none'
    }
  }

  // Check if icon is an actual asset file (from /assets/, http, or base64 data URL)
  // Only use Image element for actual SVG/image files, not color codes
  // Fallback to assetMapper when no objectType is mapped for point layers
  const resolvedIcon = mappedType?.icon || (isPointEntity || isPointLayer(layer) ? getAssetPath(layer) : undefined)
  const isActualAsset = resolvedIcon &&
    (resolvedIcon.startsWith('/assets/') ||
     resolvedIcon.startsWith('http') ||
     resolvedIcon.startsWith('data:image/'))

  // LOD: Show smaller icons at MEDIUM level
  // Point-like symbols need guaranteed visibility on main canvas.
  if (isPointEntity) {
    // Point icon size in map units (same coordinate space as parking spots)
    // getIconSize returns desired size in map units, scale converts to pixels
    const assetIconSize = getIconSize(layer)
    const mapUnitSize = isPointLayer(layer) ? assetIconSize.width : 10

    // LOD: Reduce icon size for MEDIUM level (75% of normal size)
    const lodMultiplier = lodLevel === LODLevel.MEDIUM ? 0.75 : 1.0
    const iconSize = Math.max(mapUnitSize * scale * lodMultiplier, 10)

    // Force z-index 1 for "배경" type, otherwise use mapped priority (default 5 if not set)
    const isBackground = mappedType?.name === '배경' || mappedType?.name === 'background'
    const zIndex = isBackground ? 1 : (mappedType?.priority ?? 5)

    const symbolPosition = { x: minX + (width - iconSize) / 2, y: minY + (height - iconSize) / 2 }

    if (isActualAsset) {
      const image = new shapes.standard.Image({
        id: `${layer}_${entityHandle}`,
        position: symbolPosition, // Center icon
        size: { width: iconSize, height: iconSize },
        z: zIndex,
        attrs: {
          image: {
            xlinkHref: resolvedIcon || '/assets/common.svg',
            preserveAspectRatio: 'xMidYMid meet' // Ensure icon scales properly
          },
          label: {
            text: '', // No label
          }
        },
        data: {
          layer,
          entityHandle,
          type: 'point',
          typeId: mappedType?.id,
          lodLevel,
          vertices: transformedPoints.map(p => [p.x, p.y] as [number, number]),
          originalPosition: { x: minX, y: minY },
          properties: {
            name: '',
          },
        }
      })

      // Store type ID for sync updates
      if (mappedType) {
        image.prop('objectTypeId', mappedType.id)
      }

      return image
    }

    const marker = new shapes.standard.Rectangle({
      id: `${layer}_${entityHandle}`,
      position: symbolPosition,
      size: { width: iconSize, height: iconSize },
      z: zIndex,
      attrs: {
        body: {
          fill: fillColor === 'none' ? (strokeColor || '#4CAF50') : fillColor,
          stroke: strokeColor,
          strokeWidth: 2,
          opacity: 0.95,
        },
        label: {
          text: '', // No label
        }
      },
      data: {
        layer,
        entityHandle,
        type: 'point',
        typeId: mappedType?.id,
        lodLevel,
        vertices: transformedPoints.map(p => [p.x, p.y] as [number, number]),
        originalPosition: { x: minX, y: minY },
        properties: {
          name: '',
        },
      }
    })

    // Store type ID for sync updates
    if (mappedType) {
      marker.prop('objectTypeId', mappedType.id)
    }

    return marker
  }

  // If non-point type has an actual asset icon, create Image element
  // But skip for line layers (l-lightingline etc.) — they should render as colored lines, not icons
  if (isActualAsset && !isLineEntity && !isLineLayer(layer)) {
    const isCCTV = mappedType?.name?.includes('CCTV')
    const baseIconSize = isCCTV ? 10 : 16
    const REFERENCE_SCALE = 500
    const scaleMultiplier = TARGET_WIDTH / REFERENCE_SCALE
    const lodMultiplier = lodLevel === LODLevel.MEDIUM ? 0.75 : 1.0
    const iconSize = Math.max(baseIconSize * scaleMultiplier * lodMultiplier, 10)
    const isBackground = mappedType?.name === '배경' || mappedType?.name === 'background'
    const zIndex = isBackground ? 1 : (mappedType?.priority ?? 5)

    const image = new shapes.standard.Image({
      id: `${layer}_${entityHandle}`,
      position: { x: minX + (width - iconSize) / 2, y: minY + (height - iconSize) / 2 },
      size: { width: iconSize, height: iconSize },
      z: zIndex,
      attrs: {
        image: {
          xlinkHref: mappedType?.icon || '/assets/common.svg',
          preserveAspectRatio: 'xMidYMid meet'
        },
        label: {
          text: '',
        }
      },
      data: {
        layer,
        entityHandle,
        type: isLineEntity ? 'line' : 'polygon',
        typeId: mappedType?.id,
        lodLevel,
        vertices: transformedPoints.map(p => [p.x, p.y] as [number, number]),
        originalPosition: { x: minX, y: minY },
        properties: {
          name: '',
        },
      }
    })

    if (mappedType) {
      image.prop('objectTypeId', mappedType.id)
    }

    return image
  }

  // For parking types, use simple rectangle path (bounding box only)
  // CAD data like "PARK_확장" layers contain internal diagonal lines that should not be rendered
  const isParkingType = (
    (mappedType?.name && (mappedType.name.includes('주차') || mappedType.name.toLowerCase().includes('parking'))) ||
    layer.toLowerCase().includes('park')
  )

  let pathData = ''
  if (isParkingType) {
    // Minimal-area rotated rectangle - drops internal decoration lines (the
    // reason this branch exists) without flattening diagonal stalls to their
    // axis-aligned bbox, which made neighbouring rotated stalls overlap.
    const rect = minAreaRect(transformedPoints)
    pathData = rect
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x - minX} ${p.y - minY}`)
      .join(' ') + ' Z'
  } else {
    // Build SVG path from actual entity points
    for (let i = 0; i < transformedPoints.length; i++) {
      const p = transformedPoints[i]
      const relX = p.x - minX
      const relY = p.y - minY

      if (i === 0) {
        pathData = `M ${relX} ${relY}`
      } else {
        pathData += ` L ${relX} ${relY}`
      }
    }
  }

  // Create path element
  // Force z-index 1 for "배경" type, otherwise use mapped priority (default 5 if not set)
  const isBackground = mappedType?.name === '배경' || mappedType?.name === 'background'
  const zIndex = isBackground ? 1 : (mappedType?.priority ?? 5)

  const element = new shapes.standard.Path({
    id: `${layer}_${entityHandle}`,
    position: { x: minX, y: minY },
    size: { width, height },
    z: zIndex,
    attrs: {
      root: {
        layer, // Set layer attribute on root SVG element
      },
      body: {
        d: pathData,
        fill: fillColor,
        stroke: strokeColor,
        strokeWidth: isLineEntity ? 3 : typeStrokeWidth,
        opacity: isLineEntity ? 0.9 : typeOpacity,
      },
      label: {
        text: '', // No label
      },
    },
    data: {
      layer,
      entityHandle,
      type: isPointEntity ? 'point' : (isLineEntity ? 'line' : 'polygon'),
      typeId: mappedType?.id,
      lodLevel,
      vertices: transformedPoints.map(p => [p.x, p.y] as [number, number]),
      originalPosition: { x: minX, y: minY },
      properties: {
        name: '',
        // CSV 레이어 매핑 시 선택된 주차면 종류 → router/DB type 자동 부여.
        ...(routeLayerToCategory(layer) === 'parkingLocation' && {
          type: parkingTypeFromLayer(layer),
        }),
      },
    },
  })

  // Store type ID for sync updates
  if (mappedType) {
    element.prop('objectTypeId', mappedType.id)
  }

  return element
}
